import { authGate } from './core';
import { useAuthStore } from '../../stores/authStore';

const NAMESPACE = '__apiGuard:';
export const REQ_LEDGER_PREFIX = `${NAMESPACE}reqLedger:`;
export const RELOAD_LEDGER_KEY = `${NAMESPACE}reloadLedger`;
const AUTH_STOPPED_KEY = `${NAMESPACE}authStopped`;
const MAX_CAP = 1000;

// Per-tab UUID: stable for this JS context lifetime
function genTabId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

export const TAB_ID = genTabId();
export const OWN_REQ_KEY = `${REQ_LEDGER_PREFIX}${TAB_ID}`;

// In-memory fallback (activated on localStorage throw — Safari private mode etc.)
const mem = { req: [] as number[], reload: [] as number[], active: false };

function getTs(key: string): number[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function setTs(key: string, ts: number[]): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(ts));
    return true;
  } catch {
    return false;
  }
}

function pruneAndCap(ts: number[], windowMs: number, now: number): number[] {
  const cutoff = now - windowMs;
  const pruned = ts.filter(t => t >= cutoff);
  return pruned.length > MAX_CAP ? pruned.slice(pruned.length - MAX_CAP) : pruned;
}

// ---- Request ledger (self-tab key approach) ----
// Each tab appends to its own key to avoid read-modify-write races.
// Cross-tab aggregation iterates all __apiGuard:reqLedger:* keys on read.

export function appendReqTimestamp(now: number, windowMs: number): void {
  if (mem.active) {
    mem.req = pruneAndCap([...mem.req, now], windowMs, now);
    return;
  }
  const updated = pruneAndCap([...getTs(OWN_REQ_KEY), now], windowMs, now);
  if (!setTs(OWN_REQ_KEY, updated)) {
    mem.active = true;
    mem.req = pruneAndCap([...mem.req, now], windowMs, now);
  }
}

export function countAllTabsReqTimestamps(windowMs: number, now: number = Date.now()): number {
  if (mem.active) {
    const cutoff = now - windowMs;
    return mem.req.filter(t => t >= cutoff).length;
  }
  try {
    const cutoff = now - windowMs;
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(REQ_LEDGER_PREFIX)) {
        total += getTs(k).filter(t => t >= cutoff).length;
      }
    }
    return total;
  } catch {
    return 0; // fail-open
  }
}

// ---- Reload ledger (single key, low-write, cmd-D bootstrap guard reads this) ----

export function appendReloadTimestamp(now: number, windowMs: number): void {
  if (mem.active) {
    mem.reload = pruneAndCap([...mem.reload, now], windowMs, now);
    return;
  }
  const updated = pruneAndCap([...getTs(RELOAD_LEDGER_KEY), now], windowMs, now);
  if (!setTs(RELOAD_LEDGER_KEY, updated)) {
    mem.active = true;
    mem.reload = pruneAndCap([...mem.reload, now], windowMs, now);
  }
}

export function countReloadTimestamps(windowMs: number, now: number = Date.now()): number {
  if (mem.active) {
    const cutoff = now - windowMs;
    return mem.reload.filter(t => t >= cutoff).length;
  }
  try {
    const cutoff = now - windowMs;
    return getTs(RELOAD_LEDGER_KEY).filter(t => t >= cutoff).length;
  } catch {
    return 0;
  }
}

// ---- Auth stopped persistence ----

export function markAuthStopped(): void {
  try {
    localStorage.setItem(AUTH_STOPPED_KEY, '1');
  } catch {}
}

export function clearAuthStopped(): void {
  try {
    localStorage.removeItem(AUTH_STOPPED_KEY);
  } catch {}
}

export function isAuthStoppedPersisted(): boolean {
  try {
    return localStorage.getItem(AUTH_STOPPED_KEY) === '1';
  } catch {
    return false;
  }
}

// ---- Zustand explicit wiring callback ----
// Caller (e.g. App.tsx) may pass an additional hook; the core wiring is handled inline below.

let _extraOnAuthStopped: (() => void) | null = null;

export function syncGuardStateToZustand(cb: () => void): void {
  _extraOnAuthStopped = cb;
}

// ---- Internal: handle auth-stopped signal from other tabs ----

function handleAuthStopped(): void {
  try {
    authGate.markStopped();
    useAuthStore.getState().setSessionExpired(true);
    _extraOnAuthStopped?.();
  } catch {}
}

// ---- Storage event listener (fires when OTHER tabs write to localStorage) ----

export function initStorageEventListener(): void {
  try {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (!event.key) return;
      // Explicit auth-stopped signal written by markAuthStopped()
      if (event.key === AUTH_STOPPED_KEY && event.newValue === '1') {
        handleAuthStopped();
        return;
      }
      // zustand persist writes entrybot_auth when setSessionExpired(true) is called.
      // Other tabs detect the change here (zustand persist does not auto-sync across tabs).
      if (event.key === 'entrybot_auth' && event.newValue) {
        try {
          const state = JSON.parse(event.newValue)?.state;
          if (state?.isSessionExpired === true) handleAuthStopped();
        } catch {}
      }
    });
  } catch {}
}

// Auto-init in browser environment
if (typeof window !== 'undefined') {
  initStorageEventListener();
}
